<div class="container-fluid">

  <div class="d-flex align-items-center mb-3">
    <a class="btn btn-link btn-sm p-0 me-3" routerLink="/marketplace-payments/disputes">
      <i class="bi bi-arrow-left me-1"></i>Back to disputes
    </a>
  </div>

  @if (loading) {
    <div class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>
  } @else if (loadError) {
    <div class="alert alert-danger">{{ loadError }}</div>
  } @else if (dispute) {

    <!-- Header card -->
    <div class="card mb-3">
      <div class="card-body">
        <div class="d-flex align-items-start justify-content-between mb-2">
          <div>
            <h4 class="mb-1">Dispute {{ dispute.id.slice(0, 8) }}…</h4>
            <div class="text-muted small">
              Filed {{ formatDateTime(dispute.createdAt) }} ·
              Payment {{ dispute.paymentId.slice(0, 8) }}… ·
              Job {{ dispute.jobId.slice(0, 8) }}…
            </div>
          </div>
          <span [class]="statusBadgeClass(dispute.status)">
            {{ statusDisplay(dispute.status) }}
          </span>
        </div>
        <div class="text-muted small mt-1">
          Reason: <strong>{{ reasonDisplay(dispute.reasonCode) }}</strong>
        </div>
      </div>
    </div>

    <!-- Description -->
    <div class="card mb-3">
      <div class="card-body">
        <h6 class="text-muted mb-2">Farmer's description</h6>
        <p class="mb-0" style="white-space: pre-wrap;">{{ dispute.description }}</p>
      </div>
    </div>

    <!-- Linked payment summary -->
    @if (payment) {
      <div class="card mb-3">
        <div class="card-body">
          <div class="d-flex justify-content-between align-items-start mb-2">
            <h6 class="text-muted mb-0">Linked payment</h6>
            <a [routerLink]="['/marketplace-payments/payment', payment.id]" class="btn btn-link btn-sm p-0">
              View full payment <i class="bi bi-box-arrow-up-right ms-1"></i>
            </a>
          </div>
          @if (isPreCaptureDispute) {
            <!--
              Pre-capture variant: show the ESTIMATE (locked at bid
              acceptance) and — when the pilot has reported actuals —
              the divergence. "Captured" / "Captured at" / "Refunded"
              all make no sense here (nothing was charged), so swap
              the cell grid for an estimate-vs-actuals layout that
              answers the admin's "what's at stake?" question without
              clicking through to the full payment-detail. (2026-06-11
              stg follow-up.)
            -->
            <div class="row g-3 small">
              <div class="col-md-3">
                <div class="text-muted">Status</div>
                <div class="fw-semibold">{{ payment.status }}</div>
              </div>
              <div class="col-md-3">
                <div class="text-muted">Estimated total</div>
                <div class="fw-semibold">{{ formatMoney(payment.totalChargeAmount) }}</div>
              </div>
              <div class="col-md-3">
                <div class="text-muted">Estimated acres</div>
                <div class="fw-semibold">
                  {{ formatAcres(payment.billableAcres) }}
                  <span class="text-muted">@ {{ formatMoney(payment.pricePerAcre) }}/ac</span>
                </div>
              </div>
              <div class="col-md-3">
                <div class="text-muted">Pilot reported</div>
                @if (latestReportedActuals?.billableAcres != null) {
                  <div class="fw-semibold">{{ formatAcres(latestReportedActuals!.billableAcres) }}</div>
                } @else {
                  <div class="fw-semibold text-muted">Not yet reported</div>
                }
              </div>
            </div>

            @if (hasActualsDivergence && latestReportedActuals; as reported) {
              <!--
                Defensive: revision row's totalChargeAmount can be
                NULL on rows written before the 2026-06-11 Hub fix.
                actualsRecomputedTotal falls back to acres × rate ×
                surcharge in that case so the admin still sees a real
                number instead of "— total (-$39.11 change)". When
                neither path can produce a number, we render the
                acres delta only.
              -->
              <div class="mt-3 p-2 rounded small"
                   [class.bg-warning-subtle]="reported.billableAcres! > (payment.billableAcres ?? 0)"
                   [class.bg-info-subtle]="reported.billableAcres! < (payment.billableAcres ?? 0)">
                <i class="bi bi-clipboard-check me-2"></i>
                <strong>Pilot reported {{ formatAcres(reported.billableAcres) }}</strong>
                (estimate was {{ formatAcres(payment.billableAcres) }})
                @if (actualsRecomputedTotal != null) {
                  — recomputes to {{ formatMoney(actualsRecomputedTotal) }} total
                  @if (actualsRecomputedDelta != null) {
                    ({{ actualsRecomputedDelta > 0 ? '+' : '' }}{{ formatMoney(actualsRecomputedDelta) }} vs estimate)
                  }
                }
              </div>
            }
          } @else {
            <!--
              Post-capture variant: standard 4-cell layout with the
              actual capture timestamp + refund state. Pre-capture
              variant above swaps these for estimate-vs-actuals.
            -->
            <div class="row g-3 small">
              <div class="col-md-3">
                <div class="text-muted">Status</div>
                <div class="fw-semibold">{{ payment.status }}</div>
              </div>
              <div class="col-md-3">
                <div class="text-muted">Captured</div>
                <div class="fw-semibold">{{ formatMoney(payment.totalChargeAmount) }}</div>
              </div>
              <div class="col-md-3">
                <div class="text-muted">Captured at</div>
                <div class="fw-semibold">{{ formatDateTime(payment.capturedAt) }}</div>
              </div>
              <div class="col-md-3">
                <div class="text-muted">Refunded</div>
                <div class="fw-semibold">{{ formatMoney(payment.refundAmount) }}</div>
              </div>
            </div>
          }
        </div>
      </div>
    }

    <!-- Resolution panel (open disputes) -->
    @if (isOpen) {
      <div class="card mb-3">
        <div class="card-body">
          <h5 class="mb-3">Resolve</h5>

          @if (isPreCaptureDispute) {
            <!--
              Pre-capture context strip. Surfaces the concrete dollar
              numbers the admin is choosing between so the resolution
              isn't an abstract policy decision. Each branch shows
              its dollar implication right under the radio so the
              admin can read down the column and see "$0 / $X / $Y"
              at a glance. (2026-06-11 — stg feedback: admin landed
              on this page and didn't know what to DO.)
            -->
            <div class="alert alert-info d-flex align-items-start gap-2 mb-3" role="alert">
              <i class="bi bi-info-circle-fill mt-1"></i>
              <div>
                <strong>Pre-capture dispute.</strong>
                The farmer's card has not been charged. No Stripe call
                will fire as part of this resolution — money only moves
                if you later <em>Approve</em> the row from the admin queue
                (which happens automatically under <em>Pilot wins</em>).
                <em>Split</em> isn't available; use Pilot wins, then
                Adjust the amount before approving.
              </div>
            </div>
          }

          <div class="mb-3">
            <label class="form-label fw-semibold">Resolution</label>
            <div class="form-check mb-2">
              <input
                type="radio"
                class="form-check-input"
                id="res-farmer"
                name="resolutionType"
                value="farmer_wins"
                [(ngModel)]="resolutionType"
                [disabled]="processing" />
              <label class="form-check-label" for="res-farmer">
                @if (isPreCaptureDispute) {
                  <strong>Farmer wins</strong> — cancel the agreed estimate.
                  <div class="text-muted small mt-1">
                    <i class="bi bi-arrow-right-short"></i>
                    No charge to farmer. Pilot receives nothing for the
                    work performed.
                  </div>
                } @else {
                  <strong>Farmer wins</strong> — full refund, no payout to pilot.
                }
              </label>
            </div>
            <div class="form-check mb-2">
              <input
                type="radio"
                class="form-check-input"
                id="res-pilot"
                name="resolutionType"
                value="pilot_wins"
                [(ngModel)]="resolutionType"
                [disabled]="processing" />
              <label class="form-check-label" for="res-pilot">
                @if (isPreCaptureDispute) {
                  <strong>Pilot wins</strong> — restore the row to the admin queue. Then either:
                  <div class="text-muted small mt-1">
                    <div>
                      <i class="bi bi-arrow-right-short"></i>
                      <strong>Approve</strong> as-is to capture the
                      <strong>estimate ({{ formatMoney(payment?.totalChargeAmount ?? null) }})</strong>
                    </div>
                    @if (actualsRecomputedTotal != null && actualsRecomputedDelta != null) {
                      <div>
                        <i class="bi bi-arrow-right-short"></i>
                        Or <strong>Adjust</strong> first to bill the actuals
                        (<strong>{{ formatMoney(actualsRecomputedTotal) }}</strong>,
                        {{ actualsRecomputedDelta > 0 ? '+' : '' }}{{ formatMoney(actualsRecomputedDelta) }} vs estimate),
                        then Approve
                      </div>
                    } @else if (latestReportedActuals?.billableAcres) {
                      <div>
                        <i class="bi bi-arrow-right-short"></i>
                        Or <strong>Adjust</strong> first to bill the actuals
                        ({{ formatAcres(latestReportedActuals!.billableAcres) }} reported), then Approve
                      </div>
                    }
                  </div>
                } @else {
                  <strong>Pilot wins</strong> — no refund, payout fires immediately to the pilot.
                }
              </label>
            </div>
            <!--
              Split is hidden entirely for pre-capture disputes — the
              Hub rejects it with INVALID_RESOLUTION ("use pilot_wins,
              then Adjust"). The info banner above explains the
              alternative workflow. (2026-06-11)
            -->
            @if (!isPreCaptureDispute) {
              <div class="form-check">
                <input
                  type="radio"
                  class="form-check-input"
                  id="res-split"
                  name="resolutionType"
                  value="split"
                  [(ngModel)]="resolutionType"
                  [disabled]="processing" />
                <label class="form-check-label" for="res-split">
                  <strong>Split</strong> — partial refund to the farmer, remainder fires to the pilot.
                </label>
              </div>
            }
          </div>

          @if (resolutionType === 'split') {
            <div class="mb-3">
              <label class="form-label">Refund amount ($)</label>
              <input
                type="number"
                class="form-control"
                [(ngModel)]="refundAmount"
                step="0.01"
                min="0.01"
                [attr.max]="payment?.totalChargeAmount ?? null"
                [disabled]="processing"
                name="refundAmount"
                placeholder="0.00" />
              @if (payment) {
                <small class="text-muted">
                  Maximum: {{ formatMoney(payment.totalChargeAmount) }}
                </small>
              }
            </div>
          }

          <div class="mb-3">
            <label class="form-label">Resolution notes</label>
            <textarea
              class="form-control"
              rows="3"
              [(ngModel)]="resolutionNotes"
              [disabled]="processing"
              name="resolutionNotes"
              placeholder="Optional — captured in the revision timeline for audit"></textarea>
          </div>

          @if (submitError) {
            <div class="alert alert-danger">{{ submitError }}</div>
          }

          <div class="d-flex justify-content-end">
            <button
              type="button"
              class="btn btn-primary"
              [disabled]="resolveDisabled"
              (click)="resolve()">
              @if (processing) {
                <span class="spinner-border spinner-border-sm me-2"></span>
                Resolving...
              } @else {
                <i class="bi bi-check-circle me-1"></i>Resolve dispute
              }
            </button>
          </div>
        </div>
      </div>
    } @else {
      <!-- Resolution history (resolved disputes) -->
      <div class="card mb-3">
        <div class="card-body">
          <h6 class="text-muted mb-3">Resolution</h6>
          <div class="row g-3 small">
            <div class="col-md-4">
              <div class="text-muted">Resolution type</div>
              <div class="fw-semibold">{{ resolutionTypeDisplay(dispute.resolutionType) }}</div>
            </div>
            <div class="col-md-4">
              <div class="text-muted">Amount refunded</div>
              <div class="fw-semibold">{{ formatMoney(dispute.resolutionAmount) }}</div>
            </div>
            <div class="col-md-4">
              <div class="text-muted">Resolved at</div>
              <div class="fw-semibold">{{ formatDateTime(dispute.resolvedAt) }}</div>
            </div>
            @if (dispute.resolutionNotes) {
              <div class="col-12">
                <div class="text-muted">Notes</div>
                <div class="fst-italic">"{{ dispute.resolutionNotes }}"</div>
              </div>
            }
          </div>
        </div>
      </div>
    }

  }
</div>
